import { useReducer, useEffect, useCallback } from 'react';
import { apiGetSMFactoryList, apiGetSMSupplierList } from 'api/supplier-management';
import { getListProduct } from 'api/Products/index';

const INITIAL_STATE = {
  data: {},
  hasMore: true,
  params: {
    page: 1,
    pageSize: 10,
    keywords: '',
  },
  loading: false,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        data: action.payload.list,
        pagination: action.payload.pagination,
        hasMore: action.payload.hasMore,
        error: null,
      };
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_PARAMS':
      return { ...state, params: { ...state.params, ...action.payload } };
    case 'RESET_DATA':
      return { ...state, data: [], hasMore: true };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

const SEARCHER_RESOURCES = {
  suppliers: apiGetSMSupplierList,
  factories: apiGetSMFactoryList,
  products: getListProduct,
};

export default function useSearcher(options) {
  const {
    resourceName,
    resources = SEARCHER_RESOURCES,
    transformParams,
    hasMore,
    parseResponse,
    lazyLoad = false,
  } = options;

  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const fetchData = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      /**
       * The Structured of Response should like this
       *
       * data:{
       * list: [],
       * pagination:{}
       *
       * }
       *  */
      const params = transformParams ? transformParams(state.params) : state.params;
      const response = await resources[resourceName](params);
      const parsed = parseResponse ? parseResponse(response.data) : response.data;
      if (lazyLoad) {
        const data = parsed.list || parsed.data.list;
        const merged = state.params.page <= 1 ? data : [...state.data, ...data];
        const pagination = parsed.pagination || parsed.data.pagination;
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            pagination,
            list: merged,
            hasMore: hasMore(merged, pagination),
          },
        });
        dispatch({ type: 'UPDATE_PARAMS', payload: { page: state.params.page + 1 } });
      } else {
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            pagination: parsed.pagination || parsed.data.pagination,
            list: parsed.list || parsed.data.list,
          },
        });
      }
    } catch (error) {
      dispatch({ type: 'FETCH_ERROR', payload: error });
    }
  }, [resources, resourceName, hasMore, JSON.stringify(state.params)]);

  const updateParams = (newParams) => {
    if (lazyLoad) {
      dispatch({ type: 'RESET_DATA' });
    }
    dispatch({ type: 'UPDATE_PARAMS', payload: newParams });
  };

  useEffect(() => {
    if (!lazyLoad) {
      fetchData();
    }
  }, [lazyLoad]);

  return { ...state, fetchData, updateParams };
}
